<script lang="ts">
  import type { Snippet } from 'svelte';
  import { page } from '$app/stores';
  import { isHrefExternal } from '$lib/utils/links';
  import { browser } from '$app/environment';
  import { ResponsiveVars } from '@twist/core';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import { cx } from '@emotion/css';

  interface Props {
    as?: 'a' | 'button';
    text: string;
    href?: string;
    inverse?: boolean;
    onClick?: () => void;
    name?: string;
    ariaControls?: string;
    ariaExpanded?: boolean;
    target?: string;
    rel?: string;
    children?: Snippet;
    icon?: Snippet;
    [key: string]: any;
  }

  let {
    as = 'a',
    text,
    href,
    inverse = false,
    onClick,
    name,
    ariaControls,
    ariaExpanded,
    target,
    rel,
    children,
    icon,
    ...restProps
  }: Props = $props();

  const isTouchDevice = browser && matchMedia('(hover: none) and (pointer: coarse)').matches;
  const isExternal = $derived(href && isHrefExternal(href));

  const responsiveVars = new ResponsiveVars()
    .addFluid('nav-item-font-size', toFluidVarInput(12, 12))
    .addFluid('nav-item-inner-gap', toFluidVarInput(9, 9))
    .addFluid('nav-item-y', toFluidVarInput(15, 9))
    .toCSS();
</script>

{#if as === 'button'}
  <button
    class={cx('nav-item', responsiveVars)}
    class:inverse
    class:isTouchDevice
    {name}
    id={name ? `${name}-dropdown` : undefined}
    aria-controls={ariaControls}
    aria-expanded={ariaExpanded}
    onclick={onClick}
    {...restProps}
  >
    <span class="nav-item__text" class:isTouchDevice data-text={text}>
      {text}
    </span>

    {@render children?.()}
  </button>
{:else}
  <a
    class={cx('nav-item', responsiveVars)}
    class:inverse
    class:isTouchDevice
    target={isExternal ? '_blank' : target}
    {href}
    {rel}
    onclick={onClick}
    {...restProps}
  >
    <span class="nav-item__text" class:isTouchDevice data-text={text}>
      {text}
    </span>

    {@render icon?.()}
  </a>
{/if}

<style lang="scss">
  @import 'src/scss/variables';
  @import 'src/scss/media';

  .nav-item {
    color: var(--twist-color-gray-100);
    font-family: var(--font-family-primary);
    background: none;
    border: none;
    text-decoration: none;
    outline: none;
    text-transform: uppercase;
    letter-spacing: 0.96px;
    width: max-content;
    display: inline-flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    gap: var(--nav-item-inner-gap);
    padding: var(--nav-item-y) 0;
    font-size: var(--nav-item-font-size);
    font-weight: 400;
    transition:
      background var(--twist-duration-200) ease,
      color var(--twist-duration-200) ease;

    &:active {
      opacity: 0.6;
    }

    &:hover,
    &:focus {
      font-weight: 600;
    }

    &.inverse {
      color: $text-interactive-inverse-color;
    }
  }

  .nav-item__text {
    letter-spacing: 0.96px;

    &:not(.isTouchDevice) {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: space-between;

      // prevents layout shift on hover due to font weight increase to semibold
      &::after {
        content: attr(data-text) / '';
        height: 0;
        visibility: hidden;
        overflow: hidden;
        user-select: none;
        pointer-events: none;
        font-weight: 600;

        @media speech {
          display: none;
        }
      }
    }
  }
</style>
